import React, { useState, useEffect } from "react";
import {
  Typography,
  Container,
  Box,
  InputLabel,
  Button,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  styled,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { getDealers } from "../dealer/api/dealerApi";
import { registerCustomer } from "./api/customerApi";
import { useHistory } from "react-router-dom";

const StyledFileInput = styled("input")({
  display: "none",
});

const StyledFileLabel = styled(Button)(({ theme }) => ({
  fontSize: "0.75rem",
  backgroundColor: "#f0f0f0",
  color: "#333",
  padding: "8px 16px",
  borderRadius: "4px",
  width: "100%",
  textAlign: "center",
  marginTop: "8px",
  "&:hover": {
    backgroundColor: "#e0e0e0",
  },
}));

const StyledFileName = styled("div")({
  fontSize: "0.75rem",
  marginTop: "4px",
  color: "#555",
});

const theme = createTheme({
  components: {
    MuiTextField: { defaultProps: { size: "small" } },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: "0.75rem", fontWeight: "bold" },
      },
    },
    MuiSelect: {
      defaultProps: { size: "small" },
      styleOverrides: { root: { fontSize: "0.75rem" } },
    },
    MuiButton: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          padding: "8px 16px",
          borderRadius: "8px",
          backgroundColor: "#007BFF",
          color: "#fff",
          "&:hover": { backgroundColor: "#0056b3" },
        },
      },
    },
  },
});

const RegisterCustomer = () => {
  const [dealerId, setDealerId] = useState("");
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [addressLane1, setAddressLane1] = useState("");
  const [addressLane2, setAddressLane2] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [tehsil, setTehsil] = useState("");
  const [pincode, setPincode] = useState("");
  const [fatherOrHusbandName, setFatherOrHusbandName] = useState("");
  const [motherName, setMotherName] = useState("");
  const [aadharNumber, setAadharNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [category, setCategory] = useState("");
  const [aadharDocument, setAadharDocument] = useState(null);
  const [panDocument, setPanDocument] = useState(null);
  const [categoryDocument, setCategoryDocument] = useState(null);
  const [aadharDocumentName, setAadharDocumentName] = useState("");
  const [panDocumentName, setPanDocumentName] = useState("");
  const [categoryDocumentName, setCategoryDocumentName] = useState("");
  const [dealerList, setDealerList] = useState([]);
  const history = useHistory();

  useEffect(() => {
    const loadDealers = async () => {
      try {
        const data = await getDealers();
        setDealerList(data || []);
      } catch (error) {
        console.error("Failed to load dealers:", error);
      }
    };

    loadDealers();
  }, []);

  const handleFileChange = (e, setDocument, setDocumentName) => {
    const file = e.target.files[0];
    if (file) {
      setDocument(file);
      setDocumentName(file.name);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      dealerId: Number(dealerId),
      name,
      dob,
      age: Number(age),
      gender: gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase(), // "Male" etc
      category:
        category === "GEN"
          ? "General"
          : category === "OBC"
          ? "OBC"
          : category === "SC"
          ? "SC"
          : category === "ST"
          ? "ST"
          : "",
      maritalStatus:
        maritalStatus.charAt(0).toUpperCase() + maritalStatus.slice(1),
      contact: mobileNumber,
      addressLane1,
      addressLane2,
      state,
      city,
      tehsil,
      pincode,
      fatherOrHusbandName,
      motherName,
      aadharNumber,
      panNumber,
      aadharDocument: aadharDocumentName
        ? `uploads/documents/aadhar_${aadharNumber}.jpg`
        : "",
      panDocument: panDocumentName
        ? `uploads/documents/pan_${panNumber}.jpg`
        : "",
      categoryDocument: categoryDocumentName
        ? `uploads/documents/category_${category.toLowerCase()}.pdf`
        : "",
    };

    try {
      const result = await registerCustomer(payload);
      console.log("Registration successful:", result);
      alert("Customer registered successfully!");
      history.push("/customer-overview");
    } catch (error) {
      console.error("Registration failed:", error);
      alert("Registration failed. Check console for details.");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg">
        <Box mt={3} mb={2}>
          <Typography variant="h5" component="h1" gutterBottom>
            Register Customer
          </Typography>
          <Box sx={{ backgroundColor: "#ffffff", padding: 2, borderRadius: 2 }}>
            <Grid container spacing={2}>
              {/* Dealer Dropdown */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="dealerId">
                  Dealer <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <FormControl fullWidth variant="outlined" required>
                  <Select
                    id="dealerId"
                    value={dealerId}
                    onChange={(e) => setDealerId(e.target.value)}
                    label="Dealer"
                  >
                    {dealerList.map((dealer) => (
                      <MenuItem key={dealer.id} value={dealer.id}>
                        {dealer.dealerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Name */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="name">
                  Name <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <TextField
                  id="name"
                  required
                  fullWidth
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>

              {/* DOB */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="dob">Date of Birth</InputLabel>
                <TextField
                  id="dob"
                  fullWidth
                  variant="outlined"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Age */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="age">Age</InputLabel>
                <TextField
                  id="age"
                  fullWidth
                  variant="outlined"
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                />
              </Grid>

              {/* Gender */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="gender">
                  Gender <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <FormControl fullWidth variant="outlined" required>
                  <Select
                    id="gender"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    label="Gender"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Marital Status */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="maritalStatus">Marital Status</InputLabel>
                <FormControl fullWidth variant="outlined">
                  <Select
                    id="maritalStatus"
                    value={maritalStatus}
                    onChange={(e) => setMaritalStatus(e.target.value)}
                    label="Marital Status"
                  >
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                    <MenuItem value="Divorced">Divorced</MenuItem>
                    <MenuItem value="Widowed">Widowed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Mobile Number */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="mobileNumber">Mobile Number</InputLabel>
                <TextField
                  id="mobileNumber"
                  fullWidth
                  variant="outlined"
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                />
              </Grid>

              {/* Address Lane 1 */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="addressLane1">Address Lane 1</InputLabel>
                <TextField
                  id="addressLane1"
                  fullWidth
                  variant="outlined"
                  value={addressLane1}
                  onChange={(e) => setAddressLane1(e.target.value)}
                />
              </Grid>

              {/* Address Lane 2 */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="addressLane2">Address Lane 2</InputLabel>
                <TextField
                  id="addressLane2"
                  fullWidth
                  variant="outlined"
                  value={addressLane2}
                  onChange={(e) => setAddressLane2(e.target.value)}
                />
              </Grid>

              {/* State */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="state">State</InputLabel>
                <TextField
                  id="state"
                  fullWidth
                  variant="outlined"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="city">City</InputLabel>
                <TextField
                  id="city"
                  fullWidth
                  variant="outlined"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Grid>

              {/* Tehsil */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="tehsil">Tehsil</InputLabel>
                <TextField
                  id="tehsil"
                  fullWidth
                  variant="outlined"
                  value={tehsil}
                  onChange={(e) => setTehsil(e.target.value)}
                />
              </Grid>

              {/* Pincode */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="pincode">Pincode</InputLabel>
                <TextField
                  id="pincode"
                  fullWidth
                  variant="outlined"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                />
              </Grid>

              {/* Father/Husband Name */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="fatherOrHusbandName">
                  Father/Husband Name
                </InputLabel>
                <TextField
                  id="fatherOrHusbandName"
                  fullWidth
                  variant="outlined"
                  value={fatherOrHusbandName}
                  onChange={(e) => setFatherOrHusbandName(e.target.value)}
                />
              </Grid>

              {/* Mother Name */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="motherName">Mother Name</InputLabel>
                <TextField
                  id="motherName"
                  fullWidth
                  variant="outlined"
                  value={motherName}
                  onChange={(e) => setMotherName(e.target.value)}
                />
              </Grid>

              {/* Aadhar Number */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="aadharNumber">Aadhar Number</InputLabel>
                <TextField
                  id="aadharNumber"
                  fullWidth
                  variant="outlined"
                  value={aadharNumber}
                  onChange={(e) => setAadharNumber(e.target.value)}
                />
              </Grid>

              {/* Pan Number */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="panNumber">Pan Number</InputLabel>
                <TextField
                  id="panNumber"
                  fullWidth
                  variant="outlined"
                  value={panNumber}
                  onChange={(e) => setPanNumber(e.target.value)}
                />
              </Grid>

              {/* Category */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="category">Category</InputLabel>
                <FormControl fullWidth variant="outlined">
                  <Select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="GEN">General</MenuItem>
                    <MenuItem value="OBC">OBC</MenuItem>
                    <MenuItem value="SC">SC</MenuItem>
                    <MenuItem value="ST">ST</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Aadhar Document */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="aadharDocument">
                  Aadhar Document
                </InputLabel>
                <label htmlFor="aadharDocument">
                  <StyledFileInput
                    accept="image/*"
                    id="aadharDocument"
                    type="file"
                    onChange={(e) =>
                      handleFileChange(
                        e,
                        setAadharDocument,
                        setAadharDocumentName
                      )
                    }
                  />
                  <StyledFileLabel component="span">Upload</StyledFileLabel>
                </label>
                <StyledFileName>{aadharDocumentName}</StyledFileName>
              </Grid>

              {/* Pan Document */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="panDocument">Pan Document</InputLabel>
                <label htmlFor="panDocument">
                  <StyledFileInput
                    accept="image/*"
                    id="panDocument"
                    type="file"
                    onChange={(e) =>
                      handleFileChange(e, setPanDocument, setPanDocumentName)
                    }
                  />
                  <StyledFileLabel component="span">Upload</StyledFileLabel>
                </label>
                <StyledFileName>{panDocumentName}</StyledFileName>
              </Grid>

              {/* Category Document */}
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="categoryDocument">
                  Category Document
                </InputLabel>
                <label htmlFor="categoryDocument">
                  <StyledFileInput
                    accept="application/pdf,image/*"
                    id="categoryDocument"
                    type="file"
                    onChange={(e) =>
                      handleFileChange(
                        e,
                        setCategoryDocument,
                        setCategoryDocumentName
                      )
                    }
                  />
                  <StyledFileLabel component="span">Upload</StyledFileLabel>
                </label>
                <StyledFileName>{categoryDocumentName}</StyledFileName>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleSubmit}>
                  Register Customer
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default RegisterCustomer;
